import * as React from 'react'

import { type NonFsdsTheme,resolveNonFsdsTheme } from '@/lib/non-fsds-theme'

interface NonFsdsProjectSectionProps {
  title: string
  description: string
  projects: string[]
  theme?: NonFsdsTheme | string
}

export default function NonFsdsProjectSection({ title, description, projects, theme = 'general' }: NonFsdsProjectSectionProps) {
  const themeStyles = {
    general: {
      sectionBg: 'from-sky-50/40',
      radialBg: 'bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.08),transparent_40%)]',
      cardBorder: 'border-blue-100/80',
      cardShadow: 'hover:shadow-blue-100/50',
      topGradient: 'from-blue-400 via-sky-400 to-blue-300',
      iconBg: 'from-blue-50 to-blue-100/50 ring-blue-100/50 text-blue-700',
      linkText: 'text-blue-600 group-hover:text-blue-700',
    },
    'english-academy': {
      sectionBg: 'from-blue-50/40',
      radialBg: 'bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,0.08),transparent_40%)]',
      cardBorder: 'border-blue-100/80',
      cardShadow: 'hover:shadow-emerald-100/50',
      topGradient: 'from-blue-400 via-emerald-400 to-blue-300',
      iconBg: 'from-blue-50 to-emerald-100/50 ring-emerald-100/50 text-blue-700',
      linkText: 'text-blue-600 group-hover:text-emerald-700',
    },
    programming: {
      sectionBg: 'from-blue-50/40',
      radialBg: 'bg-[radial-gradient(circle_at_top_left,rgba(99,102,241,0.08),transparent_40%)]',
      cardBorder: 'border-indigo-100/80',
      cardShadow: 'hover:shadow-indigo-100/50',
      topGradient: 'from-indigo-400 via-blue-400 to-indigo-300',
      iconBg: 'from-indigo-50 to-indigo-100/50 ring-indigo-100/50 text-indigo-700',
      linkText: 'text-indigo-600 group-hover:text-indigo-700',
    },
    math: {
      sectionBg: 'from-sky-50/40',
      radialBg: 'bg-[radial-gradient(circle_at_top_left,rgba(20,184,166,0.08),transparent_40%)]',
      cardBorder: 'border-teal-100/80',
      cardShadow: 'hover:shadow-teal-100/50',
      topGradient: 'from-teal-400 via-sky-400 to-teal-300',
      iconBg: 'from-teal-50 to-teal-100/50 ring-teal-100/50 text-teal-700',
      linkText: 'text-teal-600 group-hover:text-teal-700',
    },
  }[resolveNonFsdsTheme(theme)]

  return (
    <section
      data-testid="non-fsds-project-section"
      className={`relative overflow-hidden bg-gradient-to-b via-white to-white px-4 py-16 sm:px-6 sm:py-20 lg:py-24 ${themeStyles.sectionBg}`}
    >
      <div className={`pointer-events-none absolute inset-0 ${themeStyles.radialBg}`} />
      <div className="relative mx-auto max-w-7xl">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">{title}</h2>
          <p className="mt-4 text-lg leading-relaxed text-slate-600 max-w-2xl mx-auto">{description}</p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-8">
          {projects.map((project, index) => (
            <article
              key={project}
              className={`group relative flex flex-col overflow-hidden rounded-[24px] border bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl ${themeStyles.cardBorder} ${themeStyles.cardShadow}`}
            >
              <div className={`pointer-events-none absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r opacity-60 transition-opacity duration-300 group-hover:opacity-100 ${themeStyles.topGradient}`} />
              <div className={`mb-6 flex size-12 items-center justify-center rounded-2xl bg-gradient-to-br uppercase font-bold shadow-inner ring-1 ${themeStyles.iconBg}`}>
                {String(index + 1).padStart(2, '0')}
              </div>
              <h3 className="text-xl font-bold leading-snug text-slate-900">{project}</h3>
              <p className="mt-3 text-base leading-relaxed text-slate-600 line-clamp-3">
                Build and deploy a scalable &quot;{project.toLowerCase()}&quot; following industry standards and best practices
                from start to finish.
              </p>
              <div className="mt-auto pt-6">
                <div className={`inline-flex items-center gap-1.5 text-sm font-semibold transition-colors ${themeStyles.linkText}`}>
                  View project details
                  <svg
                    className="size-4 transition-transform duration-200 group-hover:translate-x-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2.5}
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                  </svg>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}
